"use client";

import Link from "next/link";
import { FiX, FiChevronDown, FiChevronUp } from "react-icons/fi";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/packages/context/AuthContext";
import { fetchAllCurrencies } from "@/api-service/currency";
import { fetchHeaderMenu } from "@/api-service/menu";
import { useCurrency } from "@/packages/context/CurrencyContext";
import { Currency } from "@/packages/types/currency";
import SelectField from "../common/SelectBox";
import { useFastRouter } from "@/packages/hooks/useFastRouter";
import { useSearchParams } from "next/navigation";
import LABELS from "@/components/global-constant/src/general";

interface MenuItem {
  label: string;
  href: string;
}

interface MenuChildrenGroup {
  title: string;
  items: MenuItem[];
}

interface Menu {
  label: string;
  href: string;
  status?: number;
  children?: MenuChildrenGroup[];
}

interface SideMenuProps {
  onClose: () => void;
}

export default function SideMenu({ onClose }: SideMenuProps) {
  const [categories, setCategories] = useState<Menu[]>([]);
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const { isLoggedIn } = useAuth();

  // Currency state
  const [currencyOptions, setCurrencyOptions] = useState<
    { label: string; value: string; symbol: string; exchangeRate: number; id: number }[]
  >([]);
  const { code: currencyCode, changeCurrency } = useCurrency();
  const { fastPush } = useFastRouter();
  const searchParams = useSearchParams();

  // Prevent body scroll when menu is open
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  const toggleAccordion = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  // --- Fetch Menu ---
  useEffect(() => {
    const loadMenu = async () => {
      try {
        const response = await fetchHeaderMenu();
        const transformed = transformMenuData(response.data);
        setCategories(transformed);
      } catch (error) {
        console.error("Failed to fetch menu", error);
      }
    };
    loadMenu();
  }, []);

  // --- Fetch Currencies ---
  useEffect(() => {
    const loadCurrencies = async () => {
      try {
        const res = await fetchAllCurrencies();
        const options = res.data.map((c: Currency) => ({
          label: c.code,
          value: c.code,
          symbol: c.symbol,
          exchangeRate: c.exchangeRate,
          id: c.id,
        }));
        setCurrencyOptions(options);

        const currencyFromUrl = searchParams.get("currency");
        let initialCurrency: Currency | undefined;
        if (currencyFromUrl) {
          initialCurrency = res.data.find((c) => c.code === currencyFromUrl);
        }
        if (!initialCurrency) {
          initialCurrency =
            res.data.find((c) => c.code == currencyCode) ||
            res.data.find((c) => c.isDefault) ||
            res.data[0];
        }

        if (initialCurrency) {
          changeCurrency({
            id: initialCurrency.id,
            symbol: initialCurrency.symbol,
            code: initialCurrency.code,
            exchangeRate: initialCurrency.exchangeRate,
          });
        }
      } catch (err: unknown) {
        console.error("Failed to fetch currencies", err);
      }
    };
    loadCurrencies();
  }, [searchParams]);

  // --- Handle Currency Change ---
  const handleCurrencyChange = (val: string) => {
    const selected = currencyOptions.find((c) => c.value === val);
    if (selected) {
      changeCurrency({
        id: selected.id,
        symbol: selected.symbol,
        code: selected.value,
        exchangeRate: selected.exchangeRate,
      });

      const params = new URLSearchParams(searchParams.toString());
      params.set("currency", val);
      fastPush(`?${params.toString()}`);
    }
  };

  const activeCategories = categories.filter((cat) => cat.status !== 0);

  return createPortal(
    <div className="fixed inset-0 z-50 flex">
      {/* Overlay */}
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />

      {/* Side Menu */}
      <div className="relative w-72 bg-white h-full p-5 shadow-lg animate-slide-in-left overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-lg font-bold text-primary capitalize">{LABELS.HEADER.MENU_TITLE}</h2>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <FiX className="w-6 h-6 text-primary" />
          </Button>
        </div>

        {/* Menu Items */}
        <nav className="flex flex-col gap-3 text-primary text-sm">
          {activeCategories.map((category, index) => {
            const hasChildren =
              category.children && category.children.length > 0;
            const isOpen = openIndex === index;

            return (
              <div key={index}>
                {hasChildren ? (
                  <>
                    <button
                      onClick={() => toggleAccordion(index)}
                      className="flex items-center justify-between w-full text-left hover:text-secondary"
                    >
                      <span className="flex-1 capitalize">
                        {category.label}
                      </span>
                      {isOpen ? (
                        <FiChevronUp className="w-4 h-4" />
                      ) : (
                        <FiChevronDown className="w-4 h-4" />
                      )}
                    </button>

                    {isOpen && (
                      <div className="mt-2 pl-4 flex flex-col gap-3">
                        {category.children!.map((group, gi) => {
                          if (!group.items || group.items.length === 0)
                            return null;

                          return (
                            <div key={gi}>
                              <ul className="space-y-1">
                                {group.items.map((item, ii) => (
                                  <li key={ii}>
                                    <Link
                                      href={item.href}
                                      className="block text-gray-600 hover:text-secondary text-sm capitalize"
                                      onClick={onClose}
                                    >
                                      {item.label}
                                    </Link>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </>
                ) : (
                  <Link
                    href={category.href}
                    className="block hover:text-secondary capitalize"
                    onClick={onClose}
                  >
                    {category.label}
                  </Link>
                )}
              </div>
            );
          })}
        </nav>

        {/* Footer Section */}
        <div className="mt-8 border-t pt-4 text-sm flex flex-col gap-3">
          {/* Currency Selector */}
          <div className="w-[80px]">
            <SelectField
              name="currency"
              value={currencyCode}
              onChange={handleCurrencyChange}
              options={currencyOptions.map((c) => ({
                label: c.label,
                value: c.value,
              }))}
            />
          </div>

          {!isLoggedIn ? (
            <Link
              href="/sign-in"
              onClick={onClose}
              className="hover:text-secondary"
            >
              {LABELS.HEADER.LOGIN_REGISTER}
            </Link>
          ) : (
            <Link
              href="/user-profile"
              onClick={onClose}
              className="hover:text-secondary"
            >
              {LABELS.HEADER.MY_PROFILE}
            </Link>
          )}

          <Link href="/cart" onClick={onClose} className="hover:text-secondary">
            {LABELS.HEADER.VIEW_CART}
          </Link>
        </div>
      </div>
    </div>,
    document.body
  );
}

// --- Transform Menu Based on Type ---
function transformMenuData(menuData: unknown): Menu[] {
  const getHref = (type: number, slug: string) => {
    switch (type) {
      case 1:
        return `/pages/${slug}`;
      case 2:
        return `/collections/${slug}`;
      case 3:
        return `/${slug}`;
      default:
        return `/${slug}`;
    }
  };

  const data = menuData as Record<string, unknown>;
  const dataArray = (data.data as Record<string, unknown>[]) || [];
  return dataArray.map((topCategory: Record<string, unknown>) => ({
    label: String(topCategory.name ?? ""),
    href: getHref(topCategory.type as number, String(topCategory.slug ?? "")),
    status: 1,
    children: topCategory.children
      ? ((topCategory.children as Record<string, unknown>[]) || []).map(
          (child: Record<string, unknown>) => {
            if (
              child.children &&
              (child.children as Record<string, unknown>[]).length > 0
            ) {
              return {
                title: String(child.name ?? ""),
                items: (
                  (child.children as Record<string, unknown>[]) || []
                ).map((grandchild: Record<string, unknown>) => ({
                  label: String(grandchild.name ?? ""),
                  href: getHref(
                    grandchild.type as number,
                    String(grandchild.slug ?? "")
                  ),
                })),
              };
            }
            return {
              title: "",
              items: [
                {
                  label: String(child.name ?? ""),
                  href: getHref(
                    (child.type as number) ?? 0,
                    String(child.slug ?? "")
                  ),
                },
              ],
            };
          }
        )
      : undefined,
  }));
}
